import { Button } from '@/components/shadcn/button'
import { SectionContainer } from '@/pages/shared-components/SectionContainer'
import { useRestClient } from '@/contexts/QueryProvider'
import { useSessionStorage } from '@/hooks/useSessionStorage'
import { ENDPOINTS } from '@/util'
import { useState } from 'react'
import { toast } from 'sonner'
import { useCurrentProject } from '../useCurrentProject'
import { getDatabricksErrorMessage } from '@/lib/databricksErrorMessage'
import { showDatabricksProvisionWarnings } from '@/lib/databricksProvisionWarnings'

export function ProjectDatabricksActionsSection() {
  const { projectId } = useCurrentProject()
  const [token] = useSessionStorage('token', '')
  const [isExportingPowerBi, setIsExportingPowerBi] = useState(false)
  const [isSyncingUsers, setIsSyncingUsers] = useState(false)

  const {
    getDatabricksApiGetDatabricksProjectIntegrationStatus,
    getDatabricksApiGetDatabricksProjectStatus,
    putDatabricksApiSyncProjectData,
    putDatabricksApiSyncProjectResultsToDmp,
    putDatabricksApiSyncProjectUsers,
  } = useRestClient()

  const { mutate: syncProjectData, isPending: isSyncingData } = putDatabricksApiSyncProjectData.useMutation()
  const { mutate: syncProjectResults, isPending: isSyncingResults } =
    putDatabricksApiSyncProjectResultsToDmp.useMutation()
  const { mutate: syncProjectUsers } = putDatabricksApiSyncProjectUsers.useMutation()

  const onSyncUsers = () => {
    setIsSyncingUsers(true)
    syncProjectUsers(
      { query: { projectId } },
      {
        onError: (e) => toast.error('Error syncing users: ' + getDatabricksErrorMessage(e)),
        onSuccess: (data) => {
          toast.success('Users synced')
          if (data?.warnings?.length) showDatabricksProvisionWarnings(data)
        },
        onSettled: () => {
          setIsSyncingUsers(false)
          getDatabricksApiGetDatabricksProjectStatus.invalidateQueries()
        },
      }
    )
  }

  const onSyncData = () => {
    syncProjectData(
      { query: { projectId } },
      {
        onError: (e) => toast.error('Error syncing data: ' + getDatabricksErrorMessage(e)),
        onSuccess: () => {
          toast.success('Synced project data')
          getDatabricksApiGetDatabricksProjectIntegrationStatus.invalidateQueries()
        },
      }
    )
  }

  const onSyncResults = () => {
    syncProjectResults(
      { query: { projectId } },
      {
        onError: (e) => toast.error('Error syncing results: ' + getDatabricksErrorMessage(e)),
        onSuccess: () => {
          toast.success('Synced project results')
          getDatabricksApiGetDatabricksProjectIntegrationStatus.invalidateQueries()
        },
      }
    )
  }

  const onPowerBi = async () => {
    if (!token) {
      toast.error('No token found')
      return
    }
    setIsExportingPowerBi(true)
    const response = await fetch(
      `${ENDPOINTS.DOTNET}/rest/databricks/project/integration/powerbi?projectId=${projectId}`,
      { headers: { Authorization: 'Bearer ' + token } }
    )
    if (response.ok) {
      const blob = await response.blob()
      const objectUrl = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      document.body.appendChild(anchor)
      anchor.href = objectUrl
      anchor.download = `project-${projectId}.pbids`
      anchor.click()
      window.URL.revokeObjectURL(objectUrl)
    } else {
      toast.error('Error downloading Power BI file')
    }
    setIsExportingPowerBi(false)
  }

  return (
    <SectionContainer title="Actions">
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" disabled={isSyncingUsers} onClick={onSyncUsers}>Sync users</Button>
        <Button variant="outline" disabled={isSyncingData} onClick={onSyncData}>Sync project data</Button>
        <Button variant="outline" disabled={isSyncingResults} onClick={onSyncResults}>Sync results</Button>
        <Button variant="outline" disabled={isExportingPowerBi} onClick={onPowerBi}>Export .pbids</Button>
      </div>
    </SectionContainer>
  )
}
